import React, { useEffect, useState } from 'react';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import Card from 'dashboard/components/card/Card.js';
import InputField from 'dashboard/components/fields/InputField';
import RichTextField from 'dashboard/components/fields/RichTextField';
import SwitchField from 'dashboard/components/fields/SwitchField';
import Dropzone from 'dashboard/components/upload';

/**
 * The optional introduction shown above this list on the site — a heading, a
 * sentence and a cover image.
 *
 * Folded shut by default, and that is the point. What people come to this
 * screen to do is add a blog post, a project, a news item; the list below is
 * the screen. This sat open above it, the first and largest thing on the
 * page, so every visit began by scrolling past a form for something most
 * types never use — and the same heading can be written straight onto the
 * page in the page builder, where you can see it in place.
 *
 * It stays because it is real: published, it renders above the list on the
 * live site. It just no longer greets you.
 */
export default function StageDetails(props) {
  const { stageLayout, stage, selectedBrand } = props;
  const [formData, setFormData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { setFormData(stageLayout); }, [stageLayout]);

  // Read by control type — `value ? value : checked` made an emptied text
  // field unsaveable, because the fallback for a text input is undefined.
  const handleInputChange = (e, field) => {
    const target = e?.target || {};
    const next = target.type === 'checkbox' ? target.checked : target.value;
    setFormData((current) => ({ ...current, [field]: next }));
  };
  const handleImageUpload = (file) => setImageFile(file);

  const handleCreateStageData = () => {
    const newFormData = new FormData();
    newFormData.append('data', JSON.stringify({
      stage: 'stagelayout',
      stage_name: stage,
      brand_id: selectedBrand,
      ...formData,
    }));
    if (imageFile) newFormData.append('file', imageFile);
    API.insertUpdateItemToStage(newFormData)
      .then(() => toast.success('Your data has been updated!'))
      .catch((err) => toast.error(err));
  };

  const title = `${stage?.charAt(0)?.toUpperCase() || ''}${stage?.slice(1) || ''}`;

  const written = Boolean(formData?.title || formData?.description || formData?.image);

  return (
    <Card sx={{ mb: '20px' }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        gap='10px'
        sx={{ px: '20px', cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
        role='button'
        aria-expanded={open}
      >
        <Box>
          <Typography variant='body2' fontWeight='700'>
            Introduction above the list
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {written
              ? `${formData?.published ? 'Published' : 'Not published'} — a heading and a picture shown above your ${stage} on the site.`
              : `Optional. Most people write this straight onto the page instead.`}
          </Typography>
        </Box>
        <ExpandMoreIcon
          fontSize='small'
          sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease', color: 'text.secondary' }}
        />
      </Stack>

      <Collapse in={open} unmountOnExit>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ lg: 'center' }}
        justifyContent='space-between'
        width='100%'
        px='20px'
        mb='20px'
        gap='12px'
      >
        <Box>
          <Typography variant='body2' color='text.secondary'>
            Publish it to make it appear; leave it blank for just the list.
          </Typography>
        </Box>
        <Stack direction='row' alignItems='center' spacing='16px' flexWrap='wrap' useFlexGap>
          {/* A labelled square, not an unexplained upload icon: this is the
              cover image, and at 90px there is no room for the drop target's
              own wording. */}
          <Stack spacing='4px' alignItems='center'>
            <Typography variant='caption' color='text.secondary'>Cover image</Typography>
            <Dropzone
              onFileUploaded={handleImageUpload}
              file={imageFile}
              width={90}
              height={72}
              sx={{ borderRadius: '12px' }}
              content={<CloudUploadIcon sx={{ width: 26, height: 26 }} color='action' />}
            />
          </Stack>
          {/* `reversed` and an explicit width, because SwitchField is
              width:100% with its label and switch pushed to opposite ends —
              designed for a settings column, not a toolbar. Dropped into a
              row it expanded to fill, shoved the label away from its own
              switch, and forced everything beside it onto its own line. */}
          <SwitchField
            reversed
            width='auto'
            id='published'
            label='Published'
            onChange={(e) => handleInputChange(e, 'published')}
            isChecked={formData?.published || false}
          />
          <Button
            variant='contained'
            sx={{ fontSize: '14px', borderRadius: '16px', fontWeight: 500, height: 44, flexShrink: 0 }}
            onClick={handleCreateStageData}
          >
            Update
          </Button>
        </Stack>
      </Stack>
      <Stack direction='column' spacing='20px'>
        <InputField
          mb='0px'
          me='30px'
          id='title'
          label='Heading'
          // Not defaulted to the stage key: "blogs" is a database name, and
          // showing it as the value made it look like saved content.
          value={formData?.title || ''}
          onChange={(e) => handleInputChange(e, 'title')}
          placeholder={`e.g. ${title} worth reading`}
        />
        <RichTextField
          id='description'
          label='Intro text'
          mb='0px'
          value={formData?.description || ''}
          onChange={(e) => handleInputChange(e, 'description')}
          placeholder='Your description here'
        />
      </Stack>
      </Collapse>
    </Card>
  );
}
